import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAdmin } from '../context/AdminContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Shield,
    Settings,
    Users,
    DollarSign,
    Image as ImageIcon,
    LogOut,
    Save,
    Upload,
    BarChart3,
    Loader2,
    RefreshCw,
    Search,
    Edit,
    Trash2,
    X,
    Check,
    TrendingUp,
    AlertCircle,
    Crown,
    Calendar,
    Mail,
    Send
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
    const { admin, adminLogout } = useAdmin();
    const { settings: currentSettings, refreshSettings } = useSettings();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);

    // Users state
    const [users, setUsers] = useState([]);
    const [usersTotal, setUsersTotal] = useState(0);
    const [usersPage, setUsersPage] = useState(0);
    const [usersSearch, setUsersSearch] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [editUserDialog, setEditUserDialog] = useState(false);
    const [deleteUserDialog, setDeleteUserDialog] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    // Transactions state
    const [transactions, setTransactions] = useState([]);
    const [transactionsTotal, setTransactionsTotal] = useState(0);
    const [transactionsPage, setTransactionsPage] = useState(0);

    // Analytics state
    const [analytics, setAnalytics] = useState(null);
    const [analyticsDay, setAnalyticsDays] = useState(30);

    // Settings state
    const [settings, setSettings] = useState({
        business_name: '',
        tagline: '',
        description: '',
        subscription_price: 10,
        free_uploads_limit: 1,
        currency: 'usd',
        primary_color: '#FF4F00',
        footer_text: ''
    });

    // Email Templates state
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editingTemplate, setEditingTemplate] = useState(false);
    const [testEmailModal, setTestEmailModal] = useState(false);
    const [testEmailAddress, setTestEmailAddress] = useState('');
    const [testEmailVariables, setTestEmailVariables] = useState({});
    const [bulkEmailModal, setBulkEmailModal] = useState(false);
    const [selectedTemplateForBulk, setSelectedTemplateForBulk] = useState(null);
    const [recipientGroup, setRecipientGroup] = useState('all_users');
    const [recipientCount, setRecipientCount] = useState(0);
    const [bulkEmailVariables, setBulkEmailVariables] = useState({});

    useEffect(() => {
        fetchStats();
        if (activeTab === 'users') fetchUsers();
        if (activeTab === 'transactions') fetchTransactions();
        if (activeTab === 'analytics') fetchAnalytics();
        if (activeTab === 'email-templates') fetchEmailTemplates();
    }, [activeTab]);

    useEffect(() => {
        if (currentSettings) {
            setSettings(prev => ({ ...prev, ...currentSettings }));
        }
    }, [currentSettings]);

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/admin/stats`);
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API}/admin/users`, {
                params: { skip: usersPage * 50, limit: 50, search: usersSearch }
            });
            setUsers(response.data.users);
            setUsersTotal(response.data.total);
        } catch (error) {
            toast.error('Failed to fetch users');
        }
    };

    const fetchUserDetails = async (userId) => {
        try {
            const response = await axios.get(`${API}/admin/users/${userId}`);
            setSelectedUser(response.data);
            setEditUserDialog(true);
        } catch (error) {
            toast.error('Failed to fetch user details');
        }
    };

    const handleUpdateUser = async () => {
        if (!selectedUser) return;
        setLoading(true);
        try {
            await axios.put(`${API}/admin/users/${selectedUser.id}`, {
                name: selectedUser.name,
                email: selectedUser.email,
                subscription_status: selectedUser.subscription_status,
                free_uploads_used: selectedUser.free_uploads_used
            });
            toast.success('User updated successfully');
            setEditUserDialog(false);
            fetchUsers();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update user');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!userToDelete) return;
        setLoading(true);
        try {
            await axios.delete(`${API}/admin/users/${userToDelete.id}`);
            toast.success('User deleted successfully');
            setDeleteUserDialog(false);
            setUserToDelete(null);
            fetchUsers();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelUserSubscription = async (userId, userName) => {
        if (!window.confirm(`Cancel subscription for ${userName}? They will retain access until the end of their billing period.`)) {
            return;
        }

        try {
            await axios.post(`${API}/admin/users/${userId}/cancel-subscription`);
            toast.success('Subscription canceled successfully');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
        }
    };

    const handleImpersonateUser = async (userId, userName) => {
        if (!window.confirm(`Login as ${userName}? This will log you out of the admin account.`)) {
            return;
        }

        try {
            const response = await axios.post(`${API}/admin/users/${userId}/impersonate`);

            // Store the new token
            localStorage.setItem('token', response.data.access_token);

            toast.success(`Now logged in as ${userName}`);

            // Redirect to dashboard
            window.location.href = '/dashboard';
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to impersonate user');
        }
    };

    const fetchTransactions = async () => {
        try {
            const response = await axios.get(`${API}/admin/transactions`, {
                params: { skip: transactionsPage * 50, limit: 50 }
            });
            setTransactions(response.data.transactions);
            setTransactionsTotal(response.data.total);
        } catch (error) {
            toast.error('Failed to fetch transactions');
        }
    };

    const fetchAnalytics = async () => {
        try {
            const response = await axios.get(`${API}/admin/analytics`, {
                params: { days: analyticsDay }
            });
            setAnalytics(response.data);
        } catch (error) {
            toast.error('Failed to fetch analytics');
        }
    };

    const handleSaveSettings = async () => {
        setLoading(true);
        try {
            await axios.put(`${API}/admin/settings`, settings);
            await refreshSettings();
            toast.success('Settings saved successfully!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save settings');
        } finally {
            setLoading(false);
        }
    };

    const fetchEmailTemplates = async () => {
        try {
            const response = await axios.get(`${API}/admin/email-templates`);
            setEmailTemplates(response.data);
        } catch (error) {
            toast.error('Failed to fetch email templates');
        }
    };

    const saveTemplate = async (templateData) => {
        setLoading(true);
        try {
            if (templateData.id && templateData.id !== '') {
                // Update existing
                await axios.put(`${API}/admin/email-templates/${templateData.id}`, templateData);
                toast.success('Template updated successfully');
            } else {
                // Create new
                await axios.post(`${API}/admin/email-templates`, templateData);
                toast.success('Template created successfully');
            }
            await fetchEmailTemplates();
            setEditingTemplate(false);
            setSelectedTemplate(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save template');
        } finally {
            setLoading(false);
        }
    };

    const deleteTemplate = async (templateId) => {
        if (!window.confirm('Delete this template?')) return;

        setLoading(true);
        try {
            await axios.delete(`${API}/admin/email-templates/${templateId}`);
            toast.success('Template deleted');
            await fetchEmailTemplates();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete template');
        } finally {
            setLoading(false);
        }
    };

    const sendTestEmail = async (templateId, email, variables) => {
        setLoading(true);
        try {
            await axios.post(`${API}/admin/email-templates/${templateId}/test`, {
                to_email: email,
                template_variables: variables
            });
            toast.success(`Test email sent to ${email}`);
            setTestEmailModal(false);
            setTestEmailAddress('');
            setTestEmailVariables({});
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send test email');
        } finally {
            setLoading(false);
        }
    };

    const initializeDefaultTemplates = async () => {
        if (!window.confirm('This will reset all system templates to their default values. Continue?')) return;

        setLoading(true);
        try {
            await axios.post(`${API}/admin/email-templates/initialize`);
            toast.success('Default email templates initialized successfully');
            await fetchEmailTemplates();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to initialize templates');
        } finally {
            setLoading(false);
        }
    };

    const fetchRecipientCount = async (group) => {
        try {
            const response = await axios.get(`${API}/admin/email-templates/recipient-count/${group}`);
            setRecipientCount(response.data.count);
        } catch (error) {
            console.error('Failed to fetch recipient count:', error);
            setRecipientCount(0);
        }
    };

    const handleBulkEmailGroupChange = (group) => {
        setRecipientGroup(group);
        fetchRecipientCount(group);
    };

    const sendBulkEmail = async () => {
        if (!window.confirm(`Send this email to ${recipientCount} recipients? This action cannot be undone.`)) return;

        setLoading(true);
        try {
            const response = await axios.post(`${API}/admin/email-templates/send-bulk`, {
                template_id: selectedTemplateForBulk.id,
                recipient_group: recipientGroup,
                template_variables: bulkEmailVariables
            });
            toast.success(`Bulk email sent! Sent: ${response.data.sent}, Failed: ${response.data.failed}`);
            setBulkEmailModal(false);
            setSelectedTemplateForBulk(null);
            setBulkEmailVariables({});
            setRecipientGroup('all_users');
            setRecipientCount(0);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send bulk email');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        adminLogout();
        navigate('/admin');
        toast.success('Logged out successfully');
    };

    const COLORS = ['#FF4F00', '#FF6B35', '#FF8B68', '#FFA799'];

    return (
        <div className="min-h-screen bg-background">
            {/* Grain overlay */}
            <div className="grain-overlay" />

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-2">
                            <Shield className="w-6 h-6 text-primary" />
                            <span className="font-heading font-bold text-xl">Admin Dashboard</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-muted-foreground hidden sm:block">
                                {admin?.sub}
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleLogout}
                                className="rounded-full"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Logout
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
                            <TabsTrigger value="overview" className="flex items-center gap-2">
                                <BarChart3 className="w-4 h-4" />
                                <span className="hidden sm:inline">Overview</span>
                            </TabsTrigger>
                            <TabsTrigger value="users" className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                <span className="hidden sm:inline">Users</span>
                            </TabsTrigger>
                            <TabsTrigger value="transactions" className="flex items-center gap-2">
                                <DollarSign className="w-4 h-4" />
                                <span className="hidden sm:inline">Transactions</span>
                            </TabsTrigger>
                            <TabsTrigger value="analytics" className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                <span className="hidden sm:inline">Analytics</span>
                            </TabsTrigger>
                            <TabsTrigger value="email-templates" className="flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                <span className="hidden sm:inline">Email</span>
                            </TabsTrigger>
                            <TabsTrigger value="settings" className="flex items-center gap-2">
                                <Settings className="w-4 h-4" />
                                <span className="hidden sm:inline">Settings</span>
                            </TabsTrigger>
                        </TabsList>

                        {/* Overview Tab */}
                        <TabsContent value="overview" className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold">Dashboard Overview</h2>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={fetchStats}
                                    className="rounded-full"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Refresh
                                </Button>
                            </div>

                            {stats && (
                                <>
                                    {/* Stats Cards */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div className="glass-card rounded-xl p-6 border border-border">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-muted-foreground">Total Users</span>
                                                <Users className="w-5 h-5 text-primary" />
                                            </div>
                                            <p className="text-3xl font-bold">{stats.total_users}</p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {stats.pro_users} Pro • {stats.free_users} Free
                                            </p>
                                        </div>

                                        <div className="glass-card rounded-xl p-6 border border-border">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-muted-foreground">Pro Users</span>
                                                <Crown className="w-5 h-5 text-primary" />
                                            </div>
                                            <p className="text-3xl font-bold">{stats.pro_users}</p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {((stats.pro_users / stats.total_users) * 100).toFixed(1)}% conversion
                                            </p>
                                        </div>

                                        <div className="glass-card rounded-xl p-6 border border-border">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-muted-foreground">Images Processed</span>
                                                <ImageIcon className="w-5 h-5 text-primary" />
                                            </div>
                                            <p className="text-3xl font-bold">{stats.total_images_processed}</p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {(stats.total_images_processed / stats.total_users).toFixed(1)} per user
                                            </p>
                                        </div>

                                        <div className="glass-card rounded-xl p-6 border border-border">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-muted-foreground">Revenue</span>
                                                <DollarSign className="w-5 h-5 text-primary" />
                                            </div>
                                            <p className="text-3xl font-bold">${stats.monthly_revenue?.toFixed(2)}</p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {stats.total_transactions} transactions
                                            </p>
                                        </div>
                                    </div>

                                    {/* Recent Users */}
                                    <div className="glass-card rounded-xl p-6 border border-border">
                                        <h3 className="text-lg font-semibold mb-4">Recent Users</h3>
                                        <div className="space-y-3">
                                            {stats.recent_users?.slice(0, 5).map((user) => (
                                                <div
                                                    key={user.id}
                                                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium truncate">{user.name}</p>
                                                        <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2 ml-4">
                                                        {user.subscription_status === 'active' && (
                                                            <span className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                                                                Pro
                                                            </span>
                                                        )}
                                                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                            {new Date(user.created_at).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Recent Transactions */}
                                    <div className="glass-card rounded-xl p-6 border border-border">
                                        <h3 className="text-lg font-semibold mb-4">Recent Transactions</h3>
                                        <div className="space-y-3">
                                            {stats.recent_transactions?.slice(0, 5).map((transaction) => (
                                                <div
                                                    key={transaction.id}
                                                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/50"
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-medium truncate">{transaction.user_email}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {new Date(transaction.created_at).toLocaleString()}
                                                        </p>
                                                    </div>
                                                    <div className="ml-4">
                                                        <p className="font-semibold text-primary">
                                                            ${transaction.amount?.toFixed(2)}
                                                        </p>
                                                        <p className="text-xs text-right text-muted-foreground capitalize">
                                                            {transaction.payment_status}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </TabsContent>

                        {/* Users Tab */}
                        <TabsContent value="users" className="space-y-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <h2 className="text-2xl font-bold">User Management</h2>
                                <div className="flex items-center gap-2 w-full sm:w-auto">
                                    <div className="relative flex-1 sm:w-64">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search users..."
                                            value={usersSearch}
                                            onChange={(e) => setUsersSearch(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
                                            className="pl-9"
                                        />
                                    </div>
                                    <Button
                                        onClick={fetchUsers}
                                        variant="outline"
                                        size="icon"
                                        className="rounded-full"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="glass-card rounded-xl border border-border overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-secondary/50">
                                            <tr>
                                                <th className="text-left p-4 text-sm font-semibold">User</th>
                                                <th className="text-left p-4 text-sm font-semibold hidden md:table-cell">Plan</th>
                                                <th className="text-left p-4 text-sm font-semibold hidden lg:table-cell">Credits</th>
                                                <th className="text-left p-4 text-sm font-semibold hidden xl:table-cell">Expiry/Renewal</th>
                                                <th className="text-left p-4 text-sm font-semibold hidden lg:table-cell">Joined</th>
                                                <th className="text-right p-4 text-sm font-semibold">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((user) => (
                                                <tr key={user.id} className="border-t border-border hover:bg-secondary/30">
                                                    <td className="p-4">
                                                        <div>
                                                            <p className="font-medium">{user.name}</p>
                                                            <p className="text-sm text-muted-foreground">{user.email}</p>
                                                        </div>
                                                    </td>
                                                    <td className="p-4 hidden md:table-cell">
                                                        {user.subscription_status === 'active' ? (
                                                            <div>
                                                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                                                    {user.subscription_credits_monthly || 0} credits/mo
                                                                </span>
                                                                {user.subscription_cancel_at && (
                                                                    <span className="block text-xs text-amber-600 mt-1">Canceling</span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-secondary text-muted-foreground">
                                                                Free
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="p-4 hidden lg:table-cell text-sm">
                                                        {user.credits_available || 0}
                                                    </td>
                                                    <td className="p-4 hidden xl:table-cell text-xs text-muted-foreground">
                                                        {user.subscription_cancel_at ? (
                                                            <span className="text-amber-600">
                                                                Ends: {new Date(user.subscription_cancel_at).toLocaleDateString()}
                                                            </span>
                                                        ) : user.subscription_period_end && user.subscription_status === 'active' ? (
                                                            <span>
                                                                Renews: {new Date(user.subscription_period_end).toLocaleDateString()}
                                                            </span>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </td>
                                                    <td className="p-4 hidden lg:table-cell text-sm text-muted-foreground">
                                                        {new Date(user.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="p-4">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleImpersonateUser(user.id, user.name)}
                                                                className="rounded-full"
                                                            >
                                                                Login as
                                                            </Button>
                                                            {user.subscription_status === 'active' && !user.subscription_cancel_at && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => handleCancelUserSubscription(user.id, user.name)}
                                                                    className="rounded-full text-amber-600 hover:text-amber-600"
                                                                >
                                                                    Cancel Sub
                                                                </Button>
                                                            )}
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => fetchUserDetails(user.id)}
                                                                className="rounded-full"
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => {
                                                                    setUserToDelete(user);
                                                                    setDeleteUserDialog(true);
                                                                }}
                                                                className="rounded-full text-destructive hover:text-destructive"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="flex items-center justify-between p-4 border-t border-border">
                                    <p className="text-sm text-muted-foreground">
                                        Showing {usersPage * 50 + 1} - {Math.min((usersPage + 1) * 50, usersTotal)} of {usersTotal}
                                    </p>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={usersPage === 0}
                                            onClick={() => {
                                                setUsersPage(p => p - 1);
                                                fetchUsers();
                                            }}
                                        >
                                            Previous
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={(usersPage + 1) * 50 >= usersTotal}
                                            onClick={() => {
                                                setUsersPage(p => p + 1);
                                                fetchUsers();
                                            }}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>

                        {/* Transactions Tab */}
                        <TabsContent value="transactions" className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold">Transactions</h2>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={fetchTransactions}
                                    className="rounded-full"
                                >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Refresh
                                </Button>
                            </div>

                            <div className="glass-card rounded-xl border border-border overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-secondary/50">
                                            <tr>
                                                <th className="text-left p-4 text-sm font-semibold">User</th>
                                                <th className="text-left p-4 text-sm font-semibold hidden md:table-cell">Amount</th>
                                                <th className="text-left p-4 text-sm font-semibold hidden lg:table-cell">Status</th>
                                                <th className="text-left p-4 text-sm font-semibold">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.map((transaction) => (
                                                <tr key={transaction.id} className="border-t border-border hover:bg-secondary/30">
                                                    <td className="p-4">
                                                        <div>
                                                            <p className="font-medium">{transaction.user_name || 'N/A'}</p>
                                                            <p className="text-sm text-muted-foreground">{transaction.user_email}</p>
                                                        </div>
                                                    </td>
                                                    <td className="p-4 hidden md:table-cell">
                                                        <p className="font-semibold text-primary">
                                                            ${transaction.amount?.toFixed(2)}
                                                        </p>
                                                    </td>
                                                    <td className="p-4 hidden lg:table-cell">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                            transaction.payment_status === 'completed'
                                                                ? 'bg-primary/10 text-primary'
                                                                : 'bg-secondary text-muted-foreground'
                                                        }`}>
                                                            {transaction.payment_status}
                                                        </span>
                                                    </td>
                                                    <td className="p-4 text-sm text-muted-foreground">
                                                        {new Date(transaction.created_at).toLocaleString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="flex items-center justify-between p-4 border-t border-border">
                                    <p className="text-sm text-muted-foreground">
                                        Showing {transactionsPage * 50 + 1} - {Math.min((transactionsPage + 1) * 50, transactionsTotal)} of {transactionsTotal}
                                    </p>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={transactionsPage === 0}
                                            onClick={() => {
                                                setTransactionsPage(p => p - 1);
                                                fetchTransactions();
                                            }}
                                        >
                                            Previous
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={(transactionsPage + 1) * 50 >= transactionsTotal}
                                            onClick={() => {
                                                setTransactionsPage(p => p + 1);
                                                fetchTransactions();
                                            }}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>

                        {/* Analytics Tab */}
                        <TabsContent value="analytics" className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold">Analytics & Reports</h2>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={analyticsDay}
                                        onChange={(e) => {
                                            setAnalyticsDays(Number(e.target.value));
                                            setTimeout(fetchAnalytics, 100);
                                        }}
                                        className="px-3 py-2 rounded-lg border border-border bg-background text-sm"
                                    >
                                        <option value={7}>Last 7 days</option>
                                        <option value={30}>Last 30 days</option>
                                        <option value={90}>Last 90 days</option>
                                    </select>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={fetchAnalytics}
                                        className="rounded-full"
                                    >
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Refresh
                                    </Button>
                                </div>
                            </div>

                            {analytics && (
                                <>
                                    {/* Subscription Breakdown */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        <div className="glass-card rounded-xl p-6 border border-border">
                                            <h3 className="text-lg font-semibold mb-4">Subscription Breakdown</h3>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <PieChart>
                                                    <Pie
                                                        data={[
                                                            { name: 'Free', value: analytics.subscription_breakdown.free },
                                                            { name: 'Pro', value: analytics.subscription_breakdown.pro },
                                                            { name: 'Expired', value: analytics.subscription_breakdown.expired }
                                                        ]}
                                                        cx="50%"
                                                        cy="50%"
                                                        labelLine={false}
                                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                        outerRadius={80}
                                                        fill="#8884d8"
                                                        dataKey="value"
                                                    >
                                                        {COLORS.map((color, index) => (
                                                            <Cell key={`cell-${index}`} fill={color} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>

                                        <div className="glass-card rounded-xl p-6 border border-border">
                                            <h3 className="text-lg font-semibold mb-4">Quick Stats</h3>
                                            <div className="space-y-4">
                                                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                                                    <span className="text-sm">Total Users</span>
                                                    <span className="font-bold">{analytics.subscription_breakdown.total}</span>
                                                </div>
                                                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                                                    <span className="text-sm">Free Users</span>
                                                    <span className="font-bold">{analytics.subscription_breakdown.free}</span>
                                                </div>
                                                <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10">
                                                    <span className="text-sm text-primary font-medium">Pro Users</span>
                                                    <span className="font-bold text-primary">{analytics.subscription_breakdown.pro}</span>
                                                </div>
                                                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                                                    <span className="text-sm">Conversion Rate</span>
                                                    <span className="font-bold">
                                                        {((analytics.subscription_breakdown.pro / analytics.subscription_breakdown.total) * 100).toFixed(1)}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* User Growth Chart */}
                                    <div className="glass-card rounded-xl p-6 border border-border">
                                        <h3 className="text-lg font-semibold mb-4">User Growth</h3>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart data={analytics.users_by_day}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                                <XAxis dataKey="date" stroke="#666" />
                                                <YAxis stroke="#666" />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#1a1a1a',
                                                        border: '1px solid #333',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="count"
                                                    stroke="#FF4F00"
                                                    strokeWidth={2}
                                                    name="New Users"
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Revenue Chart */}
                                    <div className="glass-card rounded-xl p-6 border border-border">
                                        <h3 className="text-lg font-semibold mb-4">Revenue Over Time</h3>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={analytics.revenue_by_day}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                                <XAxis dataKey="date" stroke="#666" />
                                                <YAxis stroke="#666" />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#1a1a1a',
                                                        border: '1px solid #333',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Legend />
                                                <Bar dataKey="revenue" fill="#FF4F00" name="Revenue ($)" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Images Processed Chart */}
                                    <div className="glass-card rounded-xl p-6 border border-border">
                                        <h3 className="text-lg font-semibold mb-4">Images Processed</h3>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart data={analytics.images_by_day}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                                <XAxis dataKey="date" stroke="#666" />
                                                <YAxis stroke="#666" />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#1a1a1a',
                                                        border: '1px solid #333',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                                <Legend />
                                                <Line
                                                    type="monotone"
                                                    dataKey="count"
                                                    stroke="#FF6B35"
                                                    strokeWidth={2}
                                                    name="Images"
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </>
                            )}
                        </TabsContent>

                        {/* Email Templates Tab */}
                        <TabsContent value="email-templates" className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-2xl font-bold">Email Templates</h2>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Manage email templates sent to users
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        onClick={initializeDefaultTemplates}
                                        variant="outline"
                                        disabled={loading}
                                        className="rounded-full"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Initializing...
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw className="mr-2 h-4 w-4" />
                                                Initialize Templates
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setSelectedTemplate({
                                                id: '',
                                                name: '',
                                                description: '',
                                                subject: '',
                                                html_body: '',
                                                text_body: '',
                                                variables: [],
                                                is_system: false
                                            });
                                            setEditingTemplate(true);
                                        }}
                                        className="rounded-full"
                                    >
                                        <Upload className="mr-2 h-4 w-4" />
                                        Create Template
                                    </Button>
                                </div>
                            </div>

                            <div className="grid gap-4">
                                {emailTemplates.map(template => (
                                    <div key={template.id} className="glass-card p-6 rounded-xl border">
                                        <div className="flex justify-between items-start gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h3 className="font-semibold text-lg">{template.name}</h3>
                                                    {template.is_system && (
                                                        <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                                                            System
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-muted-foreground mb-2">
                                                    {template.description}
                                                </p>
                                                <p className="text-xs text-muted-foreground mb-3">
                                                    <strong>Subject:</strong> {template.subject}
                                                </p>
                                                <div className="flex gap-2 flex-wrap">
                                                    {template.variables && template.variables.map(variable => (
                                                        <span
                                                            key={variable}
                                                            className="text-xs bg-secondary px-2 py-1 rounded font-mono"
                                                        >
                                                            {`{{${variable}}}`}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="flex gap-2 flex-shrink-0">
                                                <Button
                                                    size="sm"
                                                    variant="default"
                                                    onClick={() => {
                                                        setSelectedTemplateForBulk(template);
                                                        setBulkEmailVariables(
                                                            template.variables.reduce((acc, v) => ({ ...acc, [v]: '' }), {})
                                                        );
                                                        setRecipientGroup('all_users');
                                                        fetchRecipientCount('all_users');
                                                        setBulkEmailModal(true);
                                                    }}
                                                    className="rounded-full"
                                                >
                                                    <Send className="mr-2 h-4 w-4" />
                                                    Campaign
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setSelectedTemplate(template);
                                                        setTestEmailAddress('');
                                                        setTestEmailVariables(
                                                            template.variables.reduce((acc, v) => ({ ...acc, [v]: '' }), {})
                                                        );
                                                        setTestEmailModal(true);
                                                    }}
                                                    className="rounded-full"
                                                >
                                                    <Mail className="mr-2 h-4 w-4" />
                                                    Test
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setSelectedTemplate(template);
                                                        setEditingTemplate(true);
                                                    }}
                                                    className="rounded-full"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                {!template.is_system && (
                                                    <Button
                                                        size="sm"
                                                        variant="destructive"
                                                        onClick={() => deleteTemplate(template.id)}
                                                        disabled={loading}
                                                        className="rounded-full"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Template Editor Dialog */}
                            <Dialog open={editingTemplate} onOpenChange={setEditingTemplate}>
                                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                                    <DialogHeader>
                                        <DialogTitle>
                                            {selectedTemplate?.id ? 'Edit Template' : 'Create Template'}
                                        </DialogTitle>
                                        <DialogDescription>
                                            Use {`{{variable_name}}`} for dynamic content
                                        </DialogDescription>
                                    </DialogHeader>

                                    {selectedTemplate && (
                                        <div className="space-y-4">
                                            <div>
                                                <Label htmlFor="name">Template Name</Label>
                                                <Input
                                                    id="name"
                                                    value={selectedTemplate.name}
                                                    onChange={(e) => setSelectedTemplate({
                                                        ...selectedTemplate,
                                                        name: e.target.value
                                                    })}
                                                    placeholder="Welcome Email"
                                                />
                                            </div>

                                            <div>
                                                <Label htmlFor="description">Description</Label>
                                                <Input
                                                    id="description"
                                                    value={selectedTemplate.description}
                                                    onChange={(e) => setSelectedTemplate({
                                                        ...selectedTemplate,
                                                        description: e.target.value
                                                    })}
                                                    placeholder="Sent when user registers"
                                                />
                                            </div>

                                            <div>
                                                <Label htmlFor="subject">Subject Line</Label>
                                                <Input
                                                    id="subject"
                                                    value={selectedTemplate.subject}
                                                    onChange={(e) => setSelectedTemplate({
                                                        ...selectedTemplate,
                                                        subject: e.target.value
                                                    })}
                                                    placeholder="Welcome to {{business_name}}!"
                                                />
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Use variables: {`{{user_name}}, {{business_name}}`}, etc.
                                                </p>
                                            </div>

                                            <div>
                                                <Label htmlFor="html_body">HTML Body</Label>
                                                <Textarea
                                                    id="html_body"
                                                    value={selectedTemplate.html_body}
                                                    onChange={(e) => setSelectedTemplate({
                                                        ...selectedTemplate,
                                                        html_body: e.target.value
                                                    })}
                                                    rows={10}
                                                    className="font-mono text-sm"
                                                    placeholder="<html>...</html>"
                                                />
                                            </div>

                                            <div>
                                                <Label htmlFor="text_body">Plain Text Body (Fallback)</Label>
                                                <Textarea
                                                    id="text_body"
                                                    value={selectedTemplate.text_body || ''}
                                                    onChange={(e) => setSelectedTemplate({
                                                        ...selectedTemplate,
                                                        text_body: e.target.value
                                                    })}
                                                    rows={5}
                                                    placeholder="Plain text version of the email..."
                                                />
                                            </div>

                                            <div>
                                                <Label htmlFor="variables">Available Variables (comma-separated)</Label>
                                                <Input
                                                    id="variables"
                                                    value={selectedTemplate.variables?.join(', ') || ''}
                                                    onChange={(e) => setSelectedTemplate({
                                                        ...selectedTemplate,
                                                        variables: e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                                                    })}
                                                    placeholder="user_name, business_name, reset_link"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <DialogFooter>
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setEditingTemplate(false);
                                                setSelectedTemplate(null);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={() => saveTemplate(selectedTemplate)}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save Template'
                                            )}
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>

                            {/* Test Email Dialog */}
                            <Dialog open={testEmailModal} onOpenChange={setTestEmailModal}>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Send Test Email</DialogTitle>
                                        <DialogDescription>
                                            Test template: {selectedTemplate?.name}
                                        </DialogDescription>
                                    </DialogHeader>

                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="test_email">Send to Email</Label>
                                            <Input
                                                id="test_email"
                                                type="email"
                                                value={testEmailAddress}
                                                onChange={(e) => setTestEmailAddress(e.target.value)}
                                                placeholder="test@example.com"
                                            />
                                        </div>

                                        {selectedTemplate?.variables && selectedTemplate.variables.length > 0 && (
                                            <div>
                                                <Label>Template Variables</Label>
                                                <div className="space-y-2 mt-2">
                                                    {selectedTemplate.variables.map(variable => (
                                                        <div key={variable}>
                                                            <label className="block text-xs text-muted-foreground mb-1">
                                                                {`{{${variable}}}`}
                                                            </label>
                                                            <Input
                                                                value={testEmailVariables[variable] || ''}
                                                                onChange={(e) => setTestEmailVariables({
                                                                    ...testEmailVariables,
                                                                    [variable]: e.target.value
                                                                })}
                                                                placeholder={`Enter ${variable}`}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <DialogFooter>
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setTestEmailModal(false);
                                                setTestEmailAddress('');
                                                setTestEmailVariables({});
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={() => sendTestEmail(
                                                selectedTemplate?.id,
                                                testEmailAddress,
                                                testEmailVariables
                                            )}
                                            disabled={loading || !testEmailAddress}
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <Mail className="mr-2 h-4 w-4" />
                                                    Send Test Email
                                                </>
                                            )}
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>

                            {/* Bulk Email Dialog */}
                            <Dialog open={bulkEmailModal} onOpenChange={setBulkEmailModal}>
                                <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                        <DialogTitle>Send Email Campaign</DialogTitle>
                                        <DialogDescription>
                                            Template: {selectedTemplateForBulk?.name}
                                        </DialogDescription>
                                    </DialogHeader>

                                    <div className="space-y-4">
                                        <div>
                                            <Label htmlFor="recipient_group">Recipient Group</Label>
                                            <select
                                                id="recipient_group"
                                                value={recipientGroup}
                                                onChange={(e) => handleBulkEmailGroupChange(e.target.value)}
                                                className="w-full px-3 py-2 border rounded-lg bg-background"
                                            >
                                                <option value="all_users">All Users</option>
                                                <option value="active_subscribers">Active Subscribers</option>
                                                <option value="free_users">Free Users</option>
                                                <option value="expired_subscribers">Expired Subscribers</option>
                                            </select>
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {recipientCount} recipient{recipientCount !== 1 ? 's' : ''} will receive this email
                                            </p>
                                        </div>

                                        {selectedTemplateForBulk?.variables && selectedTemplateForBulk.variables.length > 0 && (
                                            <div>
                                                <Label>Template Variables</Label>
                                                <p className="text-xs text-muted-foreground mb-2">
                                                    Note: user_name and user_email are automatically filled for each recipient
                                                </p>
                                                <div className="space-y-2 mt-2">
                                                    {selectedTemplateForBulk.variables
                                                        .filter(v => v !== 'user_name' && v !== 'user_email')
                                                        .map(variable => (
                                                            <div key={variable}>
                                                                <label className="block text-xs text-muted-foreground mb-1">
                                                                    {`{{${variable}}}`}
                                                                </label>
                                                                <Input
                                                                    value={bulkEmailVariables[variable] || ''}
                                                                    onChange={(e) => setBulkEmailVariables({
                                                                        ...bulkEmailVariables,
                                                                        [variable]: e.target.value
                                                                    })}
                                                                    placeholder={`Enter ${variable} (same for all recipients)`}
                                                                />
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        )}

                                        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                                            <div className="flex items-start gap-2">
                                                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                                                        Warning: Bulk Email Campaign
                                                    </p>
                                                    <p className="text-xs text-yellow-800 dark:text-yellow-200 mt-1">
                                                        This will send {recipientCount} emails. Make sure your template is correct before proceeding.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <DialogFooter>
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setBulkEmailModal(false);
                                                setSelectedTemplateForBulk(null);
                                                setBulkEmailVariables({});
                                                setRecipientGroup('all_users');
                                                setRecipientCount(0);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={sendBulkEmail}
                                            disabled={loading || recipientCount === 0}
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="mr-2 h-4 w-4" />
                                                    Send to {recipientCount} Recipients
                                                </>
                                            )}
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </TabsContent>

                        {/* Settings Tab */}
                        <TabsContent value="settings" className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold">Application Settings</h2>
                                <Button
                                    onClick={handleSaveSettings}
                                    disabled={loading}
                                    className="rounded-full"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4 mr-2" />
                                            Save Settings
                                        </>
                                    )}
                                </Button>
                            </div>

                            <div className="grid gap-6">
                                {/* Branding */}
                                <div className="glass-card rounded-xl p-6 border border-border space-y-4">
                                    <h3 className="text-lg font-semibold">Branding</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Business Name</Label>
                                            <Input
                                                value={settings.business_name}
                                                onChange={(e) => setSettings({ ...settings, business_name: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Primary Color</Label>
                                            <Input
                                                type="color"
                                                value={settings.primary_color}
                                                onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Tagline</Label>
                                        <Input
                                            value={settings.tagline}
                                            onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Description</Label>
                                        <Textarea
                                            value={settings.description}
                                            onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                                            rows={3}
                                        />
                                    </div>
                                </div>

                                {/* Pricing */}
                                <div className="glass-card rounded-xl p-6 border border-border space-y-4">
                                    <h3 className="text-lg font-semibold">Pricing</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Pro Subscription Price</Label>
                                            <div className="flex items-center gap-2">
                                                <span className="text-2xl">$</span>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={settings.subscription_price}
                                                    onChange={(e) => setSettings({ ...settings, subscription_price: parseFloat(e.target.value) })}
                                                />
                                                <span className="text-muted-foreground">/month</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Free Uploads Limit</Label>
                                            <Input
                                                type="number"
                                                value={settings.free_uploads_limit}
                                                onChange={(e) => setSettings({ ...settings, free_uploads_limit: parseInt(e.target.value) })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="glass-card rounded-xl p-6 border border-border space-y-4">
                                    <h3 className="text-lg font-semibold">Footer</h3>
                                    <div className="space-y-2">
                                        <Label>Footer Text</Label>
                                        <Input
                                            value={settings.footer_text}
                                            onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </main>

            {/* Edit User Dialog */}
            <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>Edit User</DialogTitle>
                        <DialogDescription>
                            Make changes to user account. Click save when you're done.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedUser && (
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Name</Label>
                                <Input
                                    value={selectedUser.name}
                                    onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={selectedUser.email}
                                    onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Subscription Status</Label>
                                <select
                                    value={selectedUser.subscription_status}
                                    onChange={(e) => setSelectedUser({ ...selectedUser, subscription_status: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                                >
                                    <option value="free">Free</option>
                                    <option value="active">Pro (Active)</option>
                                    <option value="expired">Expired</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label>Free Uploads Used</Label>
                                <Input
                                    type="number"
                                    value={selectedUser.free_uploads_used}
                                    onChange={(e) => setSelectedUser({ ...selectedUser, free_uploads_used: parseInt(e.target.value) })}
                                />
                            </div>
                            <div className="pt-4 border-t border-border">
                                <p className="text-sm text-muted-foreground mb-2">User Details:</p>
                                <div className="space-y-1 text-sm">
                                    <p><span className="text-muted-foreground">User ID:</span> {selectedUser.id}</p>
                                    <p><span className="text-muted-foreground">Created:</span> {new Date(selectedUser.created_at).toLocaleString()}</p>
                                    <p><span className="text-muted-foreground">Images Processed:</span> {selectedUser.images_processed || 0}</p>
                                    <p><span className="text-muted-foreground">Transactions:</span> {selectedUser.transactions?.length || 0}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditUserDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdateUser} disabled={loading}>
                            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete User Dialog */}
            <AlertDialog open={deleteUserDialog} onOpenChange={setDeleteUserDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-destructive" />
                            Delete User
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete <strong>{userToDelete?.name}</strong>? This will permanently delete:
                            <ul className="list-disc list-inside mt-2 space-y-1">
                                <li>User account and profile</li>
                                <li>All processed image records</li>
                                <li>All transaction history</li>
                                <li>All password reset tokens</li>
                            </ul>
                            <p className="mt-3 font-semibold text-destructive">This action cannot be undone.</p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteUser}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Delete User
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default AdminDashboard;
